import { useRef, useState } from 'react'
import { Upload, Loader2, AlertTriangle, CheckCircle2, FileJson } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils/cn'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { fetchProjectFormKeys, runImport, type ImportResult, type PublishMode } from '../api/form-io'
import {
  validateEnvelope,
  planImport,
  type FormExportEnvelope,
  type ImportPlan,
} from '../api/form-io-core'

const SCOPE_LABEL: Record<string, string> = { project: 'کل پروژه', group: 'گروه فرم', form: 'فرم' }
const DEF_SRC_LABEL: Record<string, string> = { draft: 'پیش‌نویس', published: 'منتشرشده', empty: 'خالی' }

export function ImportDialog({
  projectId,
  existingGroups,
  open,
  onClose,
  onImported,
}: {
  projectId: string
  existingGroups: Array<{ id: string; title: string }>
  open: boolean
  onClose: () => void
  onImported: () => void
}) {
  const { toast } = useToast()
  const user = useAuthStore((s) => s.user)
  const fileRef = useRef<HTMLInputElement>(null)

  const [envelope, setEnvelope] = useState<FormExportEnvelope | null>(null)
  const [plan, setPlan] = useState<ImportPlan | null>(null)
  const [targetMode, setTargetMode] = useState<'new' | 'existing'>('new')
  const [targetGroupId, setTargetGroupId] = useState<string>('')
  const [publishMode, setPublishMode] = useState<PublishMode>('draft')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)

  const reset = () => {
    setEnvelope(null)
    setPlan(null)
    setTargetMode('new')
    setTargetGroupId('')
    setPublishMode('draft')
    setResult(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const close = () => {
    if (busy) return
    reset()
    onClose()
  }

  const onFile = async (file: File) => {
    let parsed: unknown
    try {
      parsed = JSON.parse(await file.text())
    } catch {
      toast({ variant: 'destructive', title: 'فایل نامعتبر', description: 'محتوای فایل JSON معتبر نیست.' })
      return
    }
    const v = validateEnvelope(parsed)
    if (!v.ok) {
      toast({ variant: 'destructive', title: 'فایل نامعتبر', description: v.error })
      return
    }
    setEnvelope(v.envelope)
    try {
      const existingKeys = await fetchProjectFormKeys(projectId)
      setPlan(planImport(v.envelope, existingKeys))
    } catch (e) {
      toast({ variant: 'destructive', title: 'خطا', description: (e as Error).message })
    }
    // Single-group envelopes can target an existing group; default to existing
    // for form scope when groups exist.
    if (v.envelope.scope === 'form' && existingGroups.length > 0) {
      setTargetMode('existing')
      setTargetGroupId(existingGroups[0].id)
    }
  }

  const canTargetExisting =
    !!envelope && envelope.groups.length === 1 && existingGroups.length > 0

  const runDisabled =
    busy || !envelope || (targetMode === 'existing' && !targetGroupId)

  const run = async () => {
    if (!envelope || !user) return
    setBusy(true)
    try {
      const res = await runImport({
        envelope,
        targetProjectId: projectId,
        userId: user.id,
        targetGroupId: targetMode === 'existing' ? targetGroupId : undefined,
        publishMode,
      })
      setResult(res)
      onImported()
      if (res.failures.length === 0) {
        toast({ title: 'ورود انجام شد', description: `${res.formsCreated} فرم ایجاد شد.` })
      } else {
        toast({
          variant: 'destructive',
          title: 'ورود با خطاهای جزئی',
          description: `${res.formsCreated} فرم ایجاد شد، ${res.failures.length} ناموفق.`,
        })
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'خطا در ورود', description: (e as Error).message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>ورود فرم از فایل</DialogTitle>
        </DialogHeader>

        {/* ── Result view ── */}
        {result ? (
          <ImportResultView result={result} />
        ) : !envelope ? (
          /* ── File picker ── */
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) onFile(f)
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-muted/20 transition-colors p-8 flex flex-col items-center gap-2"
            >
              <Upload className="w-7 h-7 text-muted-foreground" />
              <span className="text-sm font-medium">انتخاب فایل JSON</span>
              <span className="text-xs text-muted-foreground">خروجی فرم BlueDataLake</span>
            </button>
          </div>
        ) : (
          /* ── Preview + options ── */
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/30 p-3 text-sm flex items-center gap-2">
              <FileJson className="w-4 h-4 text-muted-foreground shrink-0" />
              <span>
                دامنه: <strong>{SCOPE_LABEL[envelope.scope]}</strong> — {plan?.groupCount ?? envelope.groups.length} گروه،{' '}
                {plan?.formCount ?? 0} فرم
              </span>
            </div>

            {/* Renamed-key warning */}
            {plan && plan.renamedCount > 0 && (
              <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs space-y-1.5">
                <div className="flex items-center gap-1.5 font-medium text-yellow-600 dark:text-yellow-400">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {plan.renamedCount} کلید فرم تکراری بود و تغییر نام یافت
                </div>
                {plan.forms
                  .filter((f) => f.renamed)
                  .map((f) => (
                    <div key={f.originalKey} className="font-mono text-muted-foreground">
                      {f.originalKey} → {f.finalKey}
                    </div>
                  ))}
              </div>
            )}

            {/* Form list */}
            <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border">
              {plan?.forms.map((f) => (
                <div key={f.finalKey} className="px-3 py-2 flex items-center gap-2 text-sm">
                  <span className="flex-1 truncate">{f.title.fa || f.originalKey}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
                    {f.fieldCount} فیلد
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
                    {DEF_SRC_LABEL[f.definitionSource]}
                  </span>
                </div>
              ))}
            </div>

            {/* Target group */}
            {canTargetExisting && (
              <div>
                <p className="text-sm font-medium mb-2">مقصد</p>
                <div className="flex gap-2 mb-2">
                  <Toggle active={targetMode === 'new'} onClick={() => setTargetMode('new')} label="گروه جدید" />
                  <Toggle active={targetMode === 'existing'} onClick={() => setTargetMode('existing')} label="گروه موجود" />
                </div>
                {targetMode === 'existing' && (
                  <Select value={targetGroupId} onValueChange={setTargetGroupId}>
                    <SelectTrigger>
                      <SelectValue placeholder="انتخاب گروه" />
                    </SelectTrigger>
                    <SelectContent>
                      {existingGroups.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Publish mode */}
            <div>
              <p className="text-sm font-medium mb-2">پس از ورود</p>
              <div className="flex gap-2">
                <Toggle active={publishMode === 'draft'} onClick={() => setPublishMode('draft')} label="ذخیره به‌عنوان پیش‌نویس" />
                <Toggle active={publishMode === 'publish'} onClick={() => setPublishMode('publish')} label="انتشار خودکار (۱.۰)" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {publishMode === 'draft'
                  ? 'فرم‌ها به‌عنوان پیش‌نویس ایجاد می‌شوند تا بازبینی و سپس منتشر شوند.'
                  : 'فرم‌های دارای فیلد بلافاصله در نسخهٔ ۱.۰ منتشر می‌شوند.'}
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={close}>بستن</Button>
          ) : (
            <>
              <Button variant="outline" onClick={close} disabled={busy}>
                انصراف
              </Button>
              {envelope && (
                <Button onClick={run} disabled={runDisabled} className="gap-1.5">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  ورود {plan?.formCount ?? 0} فرم
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Toggle({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-1 text-sm rounded-lg border px-3 py-2 transition-colors',
        active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-muted/30',
      )}
    >
      {label}
    </button>
  )
}

function ImportResultView({ result }: { result: ImportResult }) {
  const allOk = result.failures.length === 0
  return (
    <div className="space-y-4">
      <div
        className={cn(
          'rounded-lg p-3 flex items-center gap-2 text-sm',
          allOk ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
        )}
      >
        {allOk ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        <span>
          {result.groupsCreated} گروه و {result.formsCreated} فرم ایجاد شد
          {result.formsPublished > 0 && ` (${result.formsPublished} منتشر شد)`}
          {result.formsRenamed > 0 && `، ${result.formsRenamed} تغییر نام`}
        </span>
      </div>

      {result.failures.length > 0 && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs space-y-1">
          <p className="font-medium text-destructive">{result.failures.length} فرم ناموفق:</p>
          {result.failures.map((f, i) => (
            <div key={i} className="text-muted-foreground">
              <span className="font-mono">{f.formKey}</span>: {f.error}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
